// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import type { Shipment, StrongExclude } from '@evolvemep/api-ts';
import { Button } from '@mantine/core';

import { type ShipmentAction, useCanEditShipment } from 'modules/Shop/Shipments/ShipmentDetail/useCanEditShipment';
import { type ShipmentsContextType, useShipments } from 'modules/Shop/Shipments/ShipmentsPage/useShipments';

export type ShipmentWorkflowActionButtonProps = {
  label: string;
  action: ShipmentAction;
  contextAction: StrongExclude<keyof ShipmentsContextType, 'mutationRunning' | 'updateShipment'>;
  shipment: Shipment;
  onShipmentUpdated: () => void;
};

export const ShipmentWorkflowActionButton = ({
  label,
  action,
  contextAction,
  shipment,
  onShipmentUpdated,
}: ShipmentWorkflowActionButtonProps & {}) => {
  const { canEditShipment } = useCanEditShipment(shipment);
  const shipmentsContext = useShipments();
  const act = shipmentsContext[contextAction];
  const { mutationRunning } = shipmentsContext;
  const [saving, setSaving] = useState(false);

  const handleClick = () => {
    setSaving(true);
    void act(shipment.shipmentId)
      .then(onShipmentUpdated)
      .finally(() => setSaving(false));
  };

  if (!canEditShipment(action)) return null;

  return (
    <Button variant="light" loading={saving} disabled={mutationRunning} onClick={handleClick}>
      {label}
    </Button>
  );
};
